import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Logo from "../../common/logo";
import OrderForm from "../components/order-form";
import OrderDetails from "../components/order-details";
import UploadCsf from "../components/upload-csf";
import { useOrderService } from "../hooks/use-order-service";
import { DeliveryStatus, ShopifyOrder } from "../types";
import "./order-tracker.css";
import { mapStatusToArray } from "../../common/utils";
import TrackingProgress from "../components/tracking-progress";

const OrderTrackerScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");

  const [order, setOrder] = useState<ShopifyOrder | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStatus | null>(null);
  const [status, setStatus] = useState<string>("");
  const { fetchOrderDetails, loading, error } = useOrderService();

  // Función para cargar los detalles de la orden
  // Usamos useCallback para evitar recrear esta función en cada renderizado
  const loadOrderDetails = useCallback(async () => {
    if (!orderId) return;

    try {
      const orderResponse = await fetchOrderDetails(orderId);
      if (orderResponse) {
        setOrder(orderResponse.data);
        if (orderResponse.delivery) {
          setDelivery(orderResponse.delivery);
          setStatus(mapStatusToArray(orderResponse.delivery.status, true) as string);
        }
      }
    } catch (err) {
      console.error("Error al cargar los detalles de la orden:", err);
    }
  }, [orderId, fetchOrderDetails]);

  // Ejecutamos la carga de datos solo una vez al montar el componente
  useEffect(() => {
    loadOrderDetails();
  }, [loadOrderDetails]);

  return (
    <div className="container">
      <header className="header">
        <Logo
          src="https://alubike.com.mx/cdn/shop/files/logo_alubike_tienda_online.png?v=1708265785&width=230"
          alt="Alubike"
        />
      </header>

      <main className="main-content">
        {!orderId ? (
          <div className="error-message">
            No se ha proporcionado un ID de pedido válido.
          </div>
        ) : loading ? (
          <div className="loading">Cargando detalles de la orden...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : order ? (
          <>
            {order.status === "pending" ? (
              <>
                <h1>Confirma la dirección de tu pedido</h1>
                <br />

                <div className="order-container">
                  <OrderDetails order={order} />
                  <OrderForm
                    shippingAddress={order.shippingAddress}
                    shopifyOrderId={order.shopifyOrderId}
                    cfdi_use={order.cfdiUse}
                    payment_method={order.paymentMethod}
                    tax_regime={order.taxRegime}
                  />
                </div>
              </>
            ) : order.status === "verified" ? (
              <div>
                {!status || status === "PND" || status === "SWB" ? (
                  <div className="order-verified">
                    <h1>¡Dirección de pedido Confirmado!</h1>
                    <div className="verified-message">
                      <p>
                        Tu dirección de envío ha sido confirmada y tu pedido
                        está en proceso de envío.
                      </p>
                      <p>
                        Te mantendremos informado sobre el estado de tu entrega.
                      </p>
                    </div>
                    <div>
                      <OrderDetails order={order} />
                    </div>
                  </div>
                ) : (
                  <TrackingProgress delivery={delivery} status={status} order={order} />
                )}
                <UploadCsf 
                  orderId={order.shopifyOrderId} 
                  rfc={order.rfc}
                  csf={order.csf} 
                  cfdi_use={order.cfdiUse}
                  payment_method={order.paymentMethod}
                  tax_regime={order.taxRegime}
                />
              </div>
            ) : (
              <div className="error-message">
                Estado del pedido desconocido: {order.status}
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
};

export default OrderTrackerScreen;
